/**
 * Property Alerts Center — WHO is being emailed property alerts, and the switch.
 * Customer feedback (Aug 19): alert emails had no visible control anywhere.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import axiosInstance from '../../helpers/api';

interface AlertSub {
  searchId: string;
  leadId: string;
  leadName: string;
  leadEmail: string;
  frequency: string;
  active: boolean;
  criteria: string;
  lastSent: string | null;
  created: string | null;
}

const FREQ_BADGE: Record<string, string> = { instant: 'danger', daily: 'warning', weekly: 'info' };

const PropertyAlertsCenter: React.FC = () => {
  const [subs, setSubs] = useState<AlertSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [summary, setSummary] = useState({ total: 0, activeCount: 0, leadsBeingEmailed: 0 });

  const load = async () => {
    try {
      setLoading(true);
      const r = await axiosInstance.get('/property-alert-admin');
      if (r.data.success) {
        setSubs(r.data.subscriptions || []);
        setSummary({ total: r.data.total, activeCount: r.data.activeCount, leadsBeingEmailed: r.data.leadsBeingEmailed });
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load alert subscriptions');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSearch = async (s: AlertSub) => {
    setBusy(s.searchId);
    try {
      await axiosInstance.patch(`/property-alert-admin/search/${s.searchId}`, { active: !s.active });
      setSubs((prev) => prev.map((x) => (x.searchId === s.searchId ? { ...x, active: !s.active } : x)));
      setSummary((prev) => ({ ...prev, activeCount: prev.activeCount + (s.active ? -1 : 1) }));
    } catch { setError('Could not update that alert — try again.'); }
    finally { setBusy(null); }
  };

  const toggleLead = async (leadId: string, leadName: string, active: boolean) => {
    setBusy(leadId);
    try {
      await axiosInstance.patch(`/property-alert-admin/lead/${leadId}`, { active });
      setSubs((prev) => prev.map((x) => (x.leadId === leadId ? { ...x, active } : x)));
      await load();
    } catch { setError(`Could not update alerts for ${leadName}.`); }
    finally { setBusy(null); }
  };

  // Group by lead
  const byLead: Record<string, AlertSub[]> = {};
  subs.forEach((s) => { (byLead[s.leadId] = byLead[s.leadId] || []).push(s); });

  return (
    <div className="container py-4" style={{ maxWidth: 1000 }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h3 className="fw-bold mb-0">Property Alerts — Who's Being Emailed</h3>
      </div>
      <p className="text-muted mb-4" style={{ fontSize: 14 }}>
        Every saved search below sends listing emails to your client at the shown frequency.
        Flip a switch off and the emails stop immediately.
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="d-flex gap-3 mb-4">
        {[
          [String(summary.leadsBeingEmailed), 'clients receiving alerts'],
          [String(summary.activeCount), 'active alert searches'],
          [String(summary.total), 'total saved searches'],
        ].map(([v, l]) => (
          <Card key={l} className="flex-fill text-center border-0 shadow-sm">
            <Card.Body className="py-3">
              <div className="fs-3 fw-bold" style={{ color: '#0F1E35' }}>{v}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{l}</div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : subs.length === 0 ? (
        <Alert variant="light" className="border">No alert subscriptions found — none of your clients are receiving property alert emails.</Alert>
      ) : (
        Object.entries(byLead).map(([leadId, list]) => {
          const anyActive = list.some((s) => s.active);
          return (
            <Card key={leadId} className="mb-3 border shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                <div>
                  <strong>{list[0].leadName}</strong>{' '}
                  <span className="text-muted" style={{ fontSize: 13 }}>{list[0].leadEmail}</span>{' '}
                  <Badge bg={anyActive ? 'success' : 'secondary'}>{anyActive ? 'receiving emails' : 'paused'}</Badge>
                </div>
                <Button
                  size="sm"
                  variant={anyActive ? 'outline-danger' : 'outline-success'}
                  disabled={busy === leadId}
                  onClick={() => toggleLead(leadId, list[0].leadName, !anyActive)}
                >
                  {busy === leadId ? <Spinner size="sm" /> : anyActive ? 'Pause all' : 'Resume all'}
                </Button>
              </Card.Header>
              <Table size="sm" className="mb-0" style={{ fontSize: 13 }}>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.searchId}>
                      <td className="ps-3" style={{ width: '45%' }}>{s.criteria}</td>
                      <td><Badge bg={FREQ_BADGE[s.frequency] || 'secondary'}>{s.frequency}</Badge></td>
                      <td className="text-muted">last sent: {s.lastSent ? s.lastSent.slice(0, 10) : 'never'}</td>
                      <td className="text-end pe-3" style={{ width: 90 }}>
                        <Form.Check
                          type="switch"
                          checked={s.active}
                          disabled={busy === s.searchId}
                          onChange={() => toggleSearch(s)}
                          label=""
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default PropertyAlertsCenter;
